import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { OdataComparator } from '@pec/aion-ui-odata/types/odataComparator';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';
import { TextField, InputAdornment, CircularProgress, Popper, Paper, Link, Typography, Box } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import SearchIcon from '@material-ui/icons/Search';
import LaunchIcon from '@material-ui/icons/Launch';
import axios from 'axios';
import { DeepReadonly } from 'utility-types';
import { debounce, throttle, uniqBy } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    search: {
      width: 570
    },
    searchInput: {
      height: 35,
      color: theme.palette.common.white,
      border: 'none',
      transition: '0.3s',
      backgroundColor: fade(theme.palette.common.white, 0.15),
      '& fieldset': {
        border: 'none'
      },
      '&:hover': {
        backgroundColor: fade(theme.palette.common.white, 0.25)
      }
    },
    results: {
      width: 570,
      maxHeight: 350,
      overflowY: 'auto',
      margin: 0,
      padding: 0
    },
    listItem: {
      listStyleType: 'none',
      transition: '0.25s',
      padding: '8px 16px',
      '& a': { color: theme.palette.text.primary },
      '&:hover': { background: 'rgba(0, 110, 150, 0.078)' },
      '& svg': {
        fontSize: 16,
        verticalAlign: 'text-top',
        marginLeft: 4
      }
    }
  })
);

interface IContractor {
  id: string;
  isSubscriptionActive: boolean;
  iso2Code: string;
  iso3Code: string;
  name: string;
  primaryAddress: string;
  subscriptionType: number;
  isMain: boolean;
  companyHouseNumber: string;
}

interface ISubscription {
  countryISO2: string;
  description: string;
  id: string;
  isDeleted: boolean;
  isMain: boolean;
  legacySubscriptionTypeId: number;
}

const ITEM_PER_PAGE = 10;

type Props = {
  organization: DeepReadonly<IOrganization> | null;
};

const ContractorSearch: React.FC<Props> = ({ organization }) => {
  const [search, setSearch] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [results, setResults] = useState<IContractor[]>([]);
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState<number>(0);
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    page && fetchResults(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = useCallback(
    debounce((search: string) => {
      search.length && fetchResults(search);
    }, 300),
    []
  );

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value);
    setPage(0);
    handleSearch(value);
  };

  const handleScroll = throttle(({ scrollTop, clientHeight, scrollHeight }) => {
    if (scrollTop >= scrollHeight - clientHeight) {
      if (page * ITEM_PER_PAGE < total) {
        setPage(page + 1);
      }
    }
  }, 250);

  const fetchResults = async (search: string) => {
    try {
      setIsFetching(true);
      !page && setResults([]);
      const params = new QueryBuilder()
        .top(ITEM_PER_PAGE)
        .skip(page * ITEM_PER_PAGE)
        .orderBy('name asc')
        .filter(f => {
          f.filterBy('name', OdataComparator.Contains, search.toLowerCase()).or(f =>
            f.filterBy('CompanyHouseNumber', OdataComparator.Contains, search.toLowerCase())
          );
          if (getComplianceCountries().includes('US') && getComplianceCountries().length > 1) {
            f.and(f =>
              f
                .filterBy('subscriptionType', OdataComparator.GreaterThan, 0)
                .or(f =>
                  f
                    .filterBy('subscriptionType', OdataComparator.LessThan, 0)
                    .filterBy('isDefaultOrg', OdataComparator.Equals, true)
                )
            );
          }
          return f;
        })
        .toQueryParam();
      const { data } = await axios.get<OdataResponse<IContractor[]>>(`/api/v3.3/organizationsSearch`, { params });
      setTotal(data['@odata.count']);
      page ? setResults(prev => [...prev, ...data.value]) : setResults(data.value);
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const params = new QueryBuilder()
        .filter(f =>
          f
            .filterBy('isDeleted', OdataComparator.Equals, false)
            .filterBy('countryISO2', OdataComparator.NotEquals, 'US')
        )
        .toQueryParam();
      const { data } = await axios.get<OdataResponse<ISubscription[]>>(`/api/v3/SubscriptionTypes`, { params });
      setSubscriptions(data.value);
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  };

  const getComplianceCountries = () => {
    return [
      ...(organization?.features.includes(OrganizationFeature.ComplianceUSA) ? ['US'] : []),
      ...(organization?.features.includes(OrganizationFeature.ComplianceUK) ? ['GB'] : []),
      ...(organization?.features.includes(OrganizationFeature.ComplianceAUS) ? ['AU'] : []),
      ...(organization?.features.includes(OrganizationFeature.ComplianceGlobal) ? ['Global'] : [])
    ];
  };

  const getSubscriptionName = (id: number, isMain: boolean) => {
    if (!isMain) {
      return t('components.contractorSearch.noMembership', 'No Membership');
    }

    return subscriptions.find(s => s.legacySubscriptionTypeId === id)?.description;
  };

  return (
    <ClickAwayListener onClickAway={() => setAnchor(null)}>
      <Box>
        <TextField
          id="search-contractor"
          className={classes.search}
          placeholder={t('components.contractorSearch.searchForAContractor', 'Search for a contractor')}
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleChangeSearch}
          onFocus={event => {
            setAnchor(event.currentTarget);
            !subscriptions.length && fetchSubscriptions();
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            className: classes.searchInput
          }}
        />
        <Popper
          open={Boolean(search.length) && Boolean(anchor)}
          anchorEl={anchor}
          placement="bottom-end"
          disablePortal={true}
        >
          <Paper>
            <ul
              className={classes.results}
              onScroll={(event: React.SyntheticEvent) => handleScroll(event.currentTarget)}
            >
              {uniqBy(results, 'id').map(contractor => {
                const subscription = getSubscriptionName(contractor.subscriptionType, contractor.isMain);
                return (
                  <li key={contractor.id} className={classes.listItem}>
                    <Link href={`/SSQV4/SSQV5/Contractor/Index?id=${contractor.id}`}>
                      {`${contractor.name}${subscription ? ', ' + subscription : ''}, ${contractor.primaryAddress} ${
                        contractor.companyHouseNumber ? '(' + contractor.companyHouseNumber + ')' : ''
                      }`}
                    </Link>
                    <Link href={`/SSQV4/SSQV5/Contractor/Index?id=${contractor.id}`} target="_blank">
                      <LaunchIcon />
                    </Link>
                  </li>
                );
              })}
              {isFetching && (
                <li className={classes.listItem} style={{ textAlign: 'center' }}>
                  <CircularProgress size={18} />
                </li>
              )}
              {!isFetching && !results.length && (
                <li className={classes.listItem} style={{ textAlign: 'center' }}>
                  <Typography variant="body2">
                    {t('components.contractorSearch.noResultsFound', 'No results found')}
                  </Typography>
                </li>
              )}
            </ul>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ContractorSearch;
